import { useCallback, useEffect, useState } from 'react';
import { Plus, Copy, RotateCw, AlertTriangle, RefreshCw } from 'lucide-react';
import { apiWebhooks } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';

/**
 * Sprint 10.3 — Vue admin des Webhooks sortants.
 *
 * Toggle "Endpoints | Deliveries" dans le header :
 *  - Endpoints : table + CRUD + rotation secret (révélation 1-shot).
 *  - Deliveries : 50 dernières tentatives, filtre par status pour debug.
 *
 * Doit rester aligné avec api/src/lib/webhookEvents.ts (WEBHOOK_EVENTS).
 */

const WEBHOOK_EVENTS = [
  { value: 'timeoff.created', label: 'Demande de congé créée' },
  { value: 'timeoff.approved', label: 'Demande approuvée' },
  { value: 'timeoff.rejected', label: 'Demande refusée' },
  { value: 'timeoff.archived', label: 'Demande archivée' },
  { value: 'allocation.created', label: 'Allocation créée' },
  { value: 'allocation.updated', label: 'Allocation modifiée' },
  { value: 'allocation.archived', label: 'Allocation archivée' },
  { value: 'allocation.restored', label: 'Allocation restaurée' },
];

const DELIVERY_STATUSES = [
  { value: 'PENDING', label: 'En attente', color: 'var(--text-muted)' },
  { value: 'SUCCESS', label: 'Succès', color: 'var(--success)' },
  { value: 'FAILED', label: 'En échec', color: 'var(--warning)' },
  { value: 'DEAD', label: 'Abandonné', color: 'var(--danger)' },
];

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const pillBase = {
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-pill)',
  padding: '4px 12px',
  fontSize: 12,
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--text)',
};

const EMPTY_FORM = { name: '', url: '', events: [], enabled: true };

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatStatus(status) {
  const s = DELIVERY_STATUSES.find((x) => x.value === status);
  return s ?? { label: status, color: 'var(--text-muted)' };
}

export default function WebhooksView() {
  const [mode, setMode] = useState('endpoints'); // 'endpoints' | 'deliveries'

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Webhooks</h1>
        <div style={{ display: 'inline-flex', gap: 4 }}>
          {['endpoints', 'deliveries'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              style={{
                ...pillBase,
                background: mode === m ? 'var(--accent)' : 'transparent',
                color: mode === m ? '#fff' : 'var(--text)',
                fontWeight: mode === m ? 600 : 400,
              }}
            >
              {m === 'endpoints' ? 'Endpoints' : 'Deliveries'}
            </button>
          ))}
        </div>
      </div>

      {mode === 'endpoints' ? <EndpointsPanel /> : <DeliveriesPanel />}
    </div>
  );
}

// ============================================================================
// Endpoints panel
// ============================================================================

function EndpointsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [revealOpen, setRevealOpen] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiWebhooks.listEndpoints(archivedFilter);
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name,
      url: e.url,
      events: Array.isArray(e.events) ? e.events : [],
      enabled: e.enabled,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await apiWebhooks.updateEndpoint(editing.id, {
          name: form.name.trim(),
          url: form.url.trim(),
          events: form.events,
          enabled: form.enabled,
        });
        setModalOpen(false);
        await reload();
      } else {
        const { endpoint, secret } = await apiWebhooks.createEndpoint({
          name: form.name.trim(),
          url: form.url.trim(),
          events: form.events,
        });
        setModalOpen(false);
        setReveal({ endpoint, secret, mode: 'create' });
        setRevealOpen(true);
        await reload();
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (e) => {
    if (!window.confirm(`Archiver le webhook "${e.name}" ? Les events ne seront plus envoyés.`)) return;
    await apiWebhooks.archiveEndpoint(e.id);
    await reload();
  };

  const onRestore = async (e) => {
    await apiWebhooks.restoreEndpoint(e.id);
    await reload();
  };

  const onRotate = async (e) => {
    if (
      !window.confirm(
        `Rotater le secret HMAC de "${e.name}" ? L'ancien secret cessera immédiatement de valider les signatures.`,
      )
    )
      return;
    try {
      const { endpoint, secret } = await apiWebhooks.rotateSecret(e.id);
      setReveal({ endpoint, secret, mode: 'rotate' });
      setRevealOpen(true);
      await reload();
    } catch (err) {
      window.alert(err.message);
    }
  };

  const toggleEvent = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev],
    }));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Pas critique.
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 12,
        }}
      >
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <button type="button" onClick={openCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>
          <Plus size={14} /> Nouvel endpoint
        </button>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        Chaque endpoint reçoit un <code>POST JSON</code> signé HMAC-SHA-256 dans le header{' '}
        <code>X-Synolog-Signature: sha256=…</code> pour chaque event auquel il est abonné. Retry
        exponentiel 1min/5min/30min en cas d'échec, puis abandon.
      </p>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>URL</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Events</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Secret</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>État</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Deliveries</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 140 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Chargement…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Aucun endpoint configuré.
                </td>
              </tr>
            ) : (
              items.map((e) => {
                const events = Array.isArray(e.events) ? e.events : [];
                return (
                  <tr key={e.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 500 }}>
                      {e.name}
                      {e.archivedAt ? (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                          (archivé)
                        </span>
                      ) : null}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: 'var(--text-muted)',
                        fontFamily: 'monospace',
                        fontSize: 12,
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={e.url}
                    >
                      {e.url}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {events.map((ev) => (
                          <span
                            key={ev}
                            style={{
                              fontSize: 11,
                              padding: '2px 6px',
                              background: 'var(--glass)',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-pill)',
                              color: 'var(--text)',
                              fontFamily: 'monospace',
                            }}
                          >
                            {ev}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: 'var(--text-muted)',
                        fontFamily: 'monospace',
                        fontSize: 12,
                      }}
                    >
                      {e.secretPrefix}…
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span
                        style={{
                          fontSize: 12,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-pill)',
                          background: e.enabled ? 'var(--success)' : 'var(--text-muted)',
                          color: '#fff',
                        }}
                      >
                        {e.enabled ? 'Actif' : 'Désactivé'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 13 }}>
                      {e._count?.deliveries ?? 0}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {!e.archivedAt && (
                          <button
                            type="button"
                            onClick={() => onRotate(e)}
                            title="Rotater le secret HMAC"
                            aria-label="Rotater le secret"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-sm)',
                              padding: 6,
                              color: 'var(--warning)',
                              cursor: 'pointer',
                              display: 'inline-flex',
                            }}
                          >
                            <RotateCw size={14} />
                          </button>
                        )}
                        <EntityActions
                          archived={Boolean(e.archivedAt)}
                          onEdit={() => openEdit(e)}
                          onArchive={() => onArchive(e)}
                          onRestore={() => onRestore(e)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* --- Modale création / édition --- */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Modifier ${editing.name}` : 'Nouvel endpoint webhook'}
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="webhook-form"
              disabled={submitting || !form.name.trim() || !form.url.trim() || form.events.length === 0}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              {editing ? 'Enregistrer' : 'Créer et révéler le secret'}
            </button>
          </>
        }
      >
        <form id="webhook-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Nom
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={128}
              placeholder="Sync RH externe"
              autoFocus
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              URL de réception
            </label>
            <input
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              required
              placeholder="https://example.com/webhooks/synolog"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Events à recevoir ({form.events.length} sélectionné{form.events.length > 1 ? 's' : ''})
            </label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                padding: 10,
              }}
            >
              {WEBHOOK_EVENTS.map((ev) => {
                const active = form.events.includes(ev.value);
                return (
                  <button
                    type="button"
                    key={ev.value}
                    onClick={() => toggleEvent(ev.value)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2,
                      padding: '6px 10px',
                      background: active ? 'var(--accent)' : 'transparent',
                      color: active ? '#fff' : 'var(--text)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: 11, opacity: 0.8 }}>{ev.value}</span>
                    <span style={{ fontSize: 12 }}>{ev.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {editing && (
            <div>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--text)',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                Activé (les events seront envoyés à cette URL)
              </label>
            </div>
          )}

          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>

      {/* --- Modale "Révélation du secret HMAC" --- */}
      <Modal
        open={revealOpen}
        onClose={() => setRevealOpen(false)}
        closeOnBackdrop={false}
        size="lg"
        title={reveal?.mode === 'rotate' ? 'Nouveau secret HMAC généré' : 'Endpoint webhook créé'}
        subtitle="Configurez ce secret côté consommateur pour valider la signature des events. Il ne sera plus affiché."
        footer={
          <button type="button" onClick={() => setRevealOpen(false)} style={primaryBtn}>
            J'ai copié le secret
          </button>
        }
      >
        {reveal && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: 'rgba(255, 200, 50, 0.1)',
                border: '1px solid var(--warning)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text)',
                fontSize: 13,
              }}
            >
              <AlertTriangle size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />
              <span>
                Le consommateur doit recalculer <code>HMAC-SHA-256(payload, secret)</code> et le comparer
                au header <code>X-Synolog-Signature</code>. Stockez ce secret dans un coffre-fort.
              </span>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                Nom
              </label>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>{reveal.endpoint.name}</div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                URL
              </label>
              <code
                style={{
                  display: 'block',
                  padding: '8px 10px',
                  background: 'var(--glass)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                  color: 'var(--text)',
                  wordBreak: 'break-all',
                }}
              >
                {reveal.endpoint.url}
              </code>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 12,
                  color: 'var(--danger)',
                  fontWeight: 600,
                }}
              >
                Secret HMAC (à copier maintenant)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <code
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    background: 'var(--glass)',
                    border: '1px solid var(--danger)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 13,
                    color: 'var(--text)',
                    overflow: 'auto',
                    wordBreak: 'break-all',
                  }}
                >
                  {reveal.secret}
                </code>
                <button
                  type="button"
                  onClick={() => copyToClipboard(reveal.secret)}
                  style={{
                    ...primaryBtn,
                    background: copied ? 'var(--success)' : 'var(--accent)',
                  }}
                  title="Copier"
                >
                  <Copy size={14} />
                  {copied ? 'Copié' : 'Copier'}
                </button>
              </div>
            </div>

            <div
              style={{
                padding: 10,
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                color: 'var(--text-muted)',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
              }}
            >
              {`# Vérification de la signature (exemple Node.js)
const crypto = require('crypto');
const expected = crypto
  .createHmac('sha256', SECRET)
  .update(rawBody, 'utf8')
  .digest('hex');
// Comparer avec req.headers['x-synolog-signature'].slice(7) (strip "sha256=")`}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// ============================================================================
// Deliveries panel
// ============================================================================

function DeliveriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null); // null = tous

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiWebhooks.listDeliveries({
        status: statusFilter ?? undefined,
        limit: 50,
      });
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Filtre :</span>
        <button
          type="button"
          onClick={() => setStatusFilter(null)}
          style={{
            ...pillBase,
            background: statusFilter === null ? 'var(--accent)' : 'transparent',
            color: statusFilter === null ? '#fff' : 'var(--text)',
          }}
        >
          Tous
        </button>
        {DELIVERY_STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setStatusFilter(s.value)}
            style={{
              ...pillBase,
              background: statusFilter === s.value ? 'var(--accent)' : 'transparent',
              color: statusFilter === s.value ? '#fff' : 'var(--text)',
            }}
          >
            {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={reload}
          title="Rafraîchir"
          aria-label="Rafraîchir"
          style={{ ...secondaryBtn, marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <RefreshCw size={12} /> Rafraîchir
        </button>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Créé</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Endpoint</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Event</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Status</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Tentatives</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>HTTP</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Erreur</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Prochain retry</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Chargement…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Aucune delivery pour ce filtre.
                </td>
              </tr>
            ) : (
              items.map((d) => {
                const s = formatStatus(d.status);
                return (
                  <tr key={d.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 12 }}>
                      {formatDate(d.createdAt)}
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text)' }}>
                      {d.endpoint?.name ?? '—'}
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text)', fontFamily: 'monospace', fontSize: 12 }}>
                      {d.eventType}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-pill)',
                          background: s.color,
                          color: '#fff',
                        }}
                      >
                        {s.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{d.attemptCount}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>
                      {d.responseStatus ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        color: 'var(--text-muted)',
                        fontSize: 12,
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={d.errorMessage ?? d.responseBody ?? ''}
                    >
                      {d.errorMessage ?? '—'}
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 12 }}>
                      {formatDate(d.nextRetryAt)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
